import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Typography, Tag, Descriptions, Timeline, Input, Button, Space, Divider, Image, theme, Result, Modal, message, Spinner,
} from '../../ui';
import type { TagTone } from '../../ui';
import { Paperclip, X } from 'lucide-react';
import { request } from '../../api/client';
import { usePermissionChecker } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';
import { formatIst } from '../../lib/time';

const SEVERITY: Record<string, TagTone> = { LOW: 'blue', MED: 'gold', HIGH: 'gold', CRIT: 'red' };
const STATUS: Record<string, TagTone> = { OPEN: 'blue', IN_PROGRESS: 'gold', ESCALATED: 'red', RESOLVED: 'green', CLOSED: 'neutral', REOPENED: 'purple' };
const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin', ADMIN: 'Admin', ACCOUNTS: 'Accounts',
  SALES_HEAD: 'Sales Head', SALES_PERSON: 'Sales Person', RETAILER: 'Retailer', FARMER: 'Farmer',
};

export function ComplaintDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['complaint', id],
    queryFn: () => request<any>({ url: `/complaints/${id}` }),
  });
  const { data: comments } = useQuery({
    queryKey: ['complaint-comments', id],
    queryFn: () => request<any[]>({ url: `/complaints/${id}/comments` }),
  });
  const { data: escalations } = useQuery({
    queryKey: ['complaint-escalations', id],
    queryFn: () => request<any[]>({ url: `/complaints/${id}/escalations` }),
  });

  const addComment = useMutation({
    mutationFn: () => request({
      url: `/complaints/${id}/comments`,
      method: 'POST',
      data: { message: comment, attachments: pendingAttachments.length ? pendingAttachments : undefined },
    }),
    onSuccess: () => {
      setComment('');
      setPendingAttachments([]);
      qc.invalidateQueries({ queryKey: ['complaint-comments', id] });
    },
  });

  const hasPerm = usePermissionChecker();
  const canResolve = hasPerm('COMPLAINT.RESOLVE');
  const canEscalate = hasPerm('COMPLAINT.ESCALATE');
  // Close / Reopen go through the staff-only /status endpoint.
  const canManage = canResolve || canEscalate || hasPerm('COMPLAINT.ASSIGN');

  const isImage = (u: string) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(u);
  const onErr = (e: any) => message.error(e.response?.data?.error?.message ?? 'Action failed');
  // After any status change, refresh this complaint, the list, and the navbar badge.
  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['complaint', id] });
    qc.invalidateQueries({ queryKey: ['complaints'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const resolve = useMutation({
    mutationFn: (notes: string) => request({ url: `/complaints/${id}/resolve`, method: 'PATCH', data: { notes: notes.trim() || 'Resolved' } }),
    onSuccess: () => { message.success('Resolved'); refreshAll(); },
    onError: onErr,
  });
  // Capture resolution notes in their own prompt rather than silently reusing the
  // discussion comment box (which left the typed comment unposted).
  const askResolve = () => {
    let notes = '';
    Modal.confirm({
      title: 'Resolve complaint',
      icon: null,
      content: (
        <Input.TextArea rows={3} placeholder="Resolution notes — what was done to fix it"
          onChange={(e) => { notes = e.target.value; }} />
      ),
      okText: 'Mark resolved',
      onOk: () => resolve.mutateAsync(notes),
    });
  };
  const escalate = useMutation({
    mutationFn: () => request({ url: `/complaints/${id}/escalate`, method: 'PATCH' }),
    onSuccess: () => { message.success('Escalated'); refreshAll(); },
    onError: onErr,
  });
  const changeStatus = useMutation({
    mutationFn: (status: string) => request({ url: `/complaints/${id}/status`, method: 'PATCH', data: { status } }),
    onSuccess: (_d, status) => { message.success(`Marked ${status}`); refreshAll(); },
    onError: onErr,
  });

  // Related-party display names are resolved server-side (see complaint enrich),
  // so they show regardless of the viewer's master-data permissions.
  const orDash = (name?: string | null, id?: any) => name ?? (id ? `#${id}` : '—');

  if (isLoading) return <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>;
  if (isError || !c) {
    return (
      <Result
        status="error"
        title="Couldn't load this complaint"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/complaints')}>Back to complaints</Button>}
      />
    );
  }
  return (
    <div>
      <Typography.Title level={4}>{c.complaintNo}</Typography.Title>
      <Card>
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="Severity"><Tag tone={SEVERITY[c.severity]}>{c.severity}</Tag></Descriptions.Item>
          <Descriptions.Item label="Status"><Tag tone={STATUS[c.status]}>{c.status}</Tag></Descriptions.Item>
          <Descriptions.Item label="Source">{c.source}</Descriptions.Item>
          {(c.retailerId || c.farmerId) && (
            <Descriptions.Item label="Raised for">
              <Space size={6}>
                <span>{c.retailerId ? orDash(c.retailerName, c.retailerId) : orDash(c.farmerName, c.farmerId)}</span>
                <Tag tone={c.retailerId ? 'blue' : 'green'}>{c.retailerId ? 'Retailer' : 'Farmer'}</Tag>
              </Space>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Raised by">
            <Space size={6}>
              <span>{orDash(c.raisedByName, c.raisedByUserId)}</span>
              {c.raisedByRole && <Tag tone="blue">{ROLE_LABEL[c.raisedByRole] ?? c.raisedByRole}</Tag>}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Category">{c.category}</Descriptions.Item>
          <Descriptions.Item label="SLA Due">{formatIst(c.slaDueAt)}</Descriptions.Item>
          <Descriptions.Item label="Assignee">
            {c.assignedToUserId ? (
              <Space size={6}>
                <span>{orDash(c.assignedToName, c.assignedToUserId)}</span>
                {c.assignedToRole && <Tag tone="blue">{ROLE_LABEL[c.assignedToRole] ?? c.assignedToRole}</Tag>}
              </Space>
            ) : '—'}
          </Descriptions.Item>
          {c.productId && <Descriptions.Item label="Product">{orDash(c.productName, c.productId)}</Descriptions.Item>}
          {c.batchNo && <Descriptions.Item label="Batch">{c.batchNo}</Descriptions.Item>}
          <Descriptions.Item label="Description" span={3}>{c.description}</Descriptions.Item>
          {c.resolvedAt && (
            <Descriptions.Item label="Resolution" span={3}>
              {c.resolutionNotes ?? '—'}{' '}
              <span style={{ color: token.colorTextTertiary }}>({formatIst(c.resolvedAt)})</span>
            </Descriptions.Item>
          )}
          {c.customerRating ? (
            <Descriptions.Item label="Customer rating" span={3}>
              <span style={{ color: '#E7B22C' }}>{'★'.repeat(c.customerRating)}{'☆'.repeat(5 - c.customerRating)}</span>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
        <Divider />
        <Space wrap>
          {/* A new (OPEN) or reopened complaint must be moved to In Progress before it can be resolved. */}
          {canManage && ['OPEN', 'REOPENED'].includes(c.status) && (
            <Button variant="primary" onClick={() => changeStatus.mutate('IN_PROGRESS')} loading={changeStatus.isPending}>
              Mark In Progress
            </Button>
          )}
          {canResolve && ['IN_PROGRESS', 'ESCALATED'].includes(c.status) && (
            <Button variant="primary" onClick={askResolve} loading={resolve.isPending}>Resolve</Button>
          )}
          {canEscalate && !['RESOLVED', 'CLOSED', 'ESCALATED'].includes(c.status) && (
            <Button variant="danger" onClick={() => escalate.mutate()} loading={escalate.isPending}>Escalate</Button>
          )}
          {canManage && c.status === 'RESOLVED' && (
            <Button variant="default" onClick={() => changeStatus.mutate('CLOSED')} loading={changeStatus.isPending}>Close</Button>
          )}
          {canManage && ['RESOLVED', 'CLOSED'].includes(c.status) && (
            <Button variant="default" onClick={() => changeStatus.mutate('REOPENED')} loading={changeStatus.isPending}>Reopen</Button>
          )}
          {!canManage && (
            <Typography.Text type="secondary">Status is managed by the support team.</Typography.Text>
          )}
        </Space>
      </Card>

      <Card title="Discussion" className="mt-4">
        <Timeline
          items={(comments ?? []).map((com: any) => ({
            color: 'blue',
            children: (
              <div>
                <b>{com.authorName ?? `User ${com.authorUserId}`}</b> · {formatIst(com.createdAt)}
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{com.message}</div>
                {(com.attachments ?? []).length > 0 && (
                  <Space size={6} wrap style={{ marginTop: 6 }}>
                    {(com.attachments as string[]).map((url) =>
                      isImage(url) ? (
                        <Image key={url} src={url} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 6, border: `1px solid ${token.colorBorderSecondary}` }} />
                      ) : (
                        <Button key={url} variant="default" size="sm" icon={<Paperclip size={16} />} onClick={() => window.open(url, '_blank', 'noopener')}>
                          Attachment
                        </Button>
                      ),
                    )}
                  </Space>
                )}
              </div>
            ),
          }))}
        />
        <Input.TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment…"
          rows={2}
          style={{ marginBottom: 8 }}
        />
        {pendingAttachments.length > 0 && (
          <Space size={6} wrap style={{ marginBottom: 8 }}>
            {pendingAttachments.map((url) => (
              <Tag key={url}>
                <Paperclip size={16} />
                {url.split('/').pop()}
                <X
                  size={16}
                  className="cursor-pointer"
                  onClick={() => setPendingAttachments((arr) => arr.filter((u) => u !== url))}
                />
              </Tag>
            ))}
          </Space>
        )}
        <Space>
          <FileUploadField
            category="attachment"
            buttonLabel="Attach file"
            preview={false}
            allowUrlPaste={false}
            value=""
            onChange={(url) => { if (url) setPendingAttachments((arr) => [...arr, url]); }}
          />
          <Button
            variant="primary"
            onClick={() => addComment.mutate()}
            disabled={!comment && pendingAttachments.length === 0}
            loading={addComment.isPending}
          >
            Post
          </Button>
        </Space>
      </Card>

      <Card title="Escalations" className="mt-4">
        <Timeline
          items={(escalations ?? []).map((e: any) => ({
            color: e.level === 'L3' ? 'red' : e.level === 'L2' ? 'orange' : 'blue',
            children: `${e.level} · ${formatIst(e.createdAt)} · ${e.reason ?? ''}`,
          }))}
        />
      </Card>
    </div>
  );
}
